import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from "@react-google-maps/api";
import toast from "react-hot-toast";
import { Formik, Form } from "formik";
import * as attendanceService from "../../Services/attendanceService";
import { FloatingInput, Spinner } from "../../Components/Common";
import { UserContext } from "../../Context";
import {
  ACTION_APPROVED,
  ACTION_AUTO_APPROVED,
  ACTION_REJECTED,
  ERROR_FETCHING_GEOFENCE,
  MESSAGES,
} from "./constants";
import { ATTENDANCE_STATUS_ERROR } from "./constants";
import { HiViewfinderCircle } from "react-icons/hi2";
import { BiHide } from "react-icons/bi";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { ROLES } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import * as turf from "@turf/turf";
import { parseWKTPolygon } from "../../Utils/geoUtils";
import { getSiteDetailsById } from "../../Services/siteServices";

const libraries = ["places"];
const DetailedViewPage = () => {
  const { id } = useParams();
  const [attendance, setAttendance] = useState({
    site_name: "",
    user_name: "",
    date: "",
    checkin_time: "",
    checkout_time: "",
    is_backdated: "",
    status: "",
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { nonStaffAccess, user } = useContext(UserContext);
  const [showCheckinPhoto, setShowCheckinPhoto] = useState(false);
  const [showCheckoutPhoto, setShowCheckoutPhoto] = useState(false);
  const [sitePolygon, setSitePolygon] = useState([]);

  // For Google Map
  const containerStyle = {
    width: "100%",
    height: "400px",
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handleApproval = async (id, action) => {
    try {
      const response = await attendanceService.reviewAttendance(id, {
        action: action,
      });
      if (response.status === 200) {
        toast.success(MESSAGES.ENTRY_SUBMITTED);
        await fetchAttendance();
      } else {
        const msg = extractErrorMessage(response) || ATTENDANCE_STATUS_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ATTENDANCE_STATUS_ERROR;
      toast.error(msg);
    }
  };

  const getAttendanceData = async () => {
    try {
      const response = await attendanceService.getDetailedViewById(id);

      if (response?.status === 200 && response.data) {
        setAttendance(response.data);
      } else {
        setAttendance(null);
      }
    } catch (error) {
      setAttendance(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAttendanceData();
  }, [id]);

  const fetchSiteGeofence = async (siteId) => {
    try {
      const res = await getSiteDetailsById(siteId);
      if (res?.status === 200 && res.data?.geofence) {
        const coords = parseWKTPolygon(res.data.geofence);
        setSitePolygon(coords);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || ERROR_FETCHING_GEOFENCE;
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (attendance?.site) {
      fetchSiteGeofence(attendance.site);
    }
  }, [attendance]);

  const canApproveReject = () => {
    if (!nonStaffAccess) return false;

    const status = attendance.status;

    if (
      status === "PENDING_L1_APPROVAL" &&
      user?.role === ROLES.PROJECT_ENGINEER
    )
      return true;
    if (
      status === "PENDING_L2_APPROVAL" &&
      user?.role === ROLES.PROJECT_MANAGER
    )
      return true;

    return false;
  };

  const fetchAttendance = async () => {
    try {
      const response = await attendanceService.getDetailedViewById(id);
      if (response?.status === 200 && response.data) {
        setAttendance(response.data);
      } else {
        setAttendance(null);
      }
    } catch (error) {
      setAttendance(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [id]);

  if (loading)
    return (
      <div className="flex justify-center pt-10">
        <Spinner size={4} />
      </div>
    );
  if (!attendance)
    return <div className="p-4 text-red-500">{MESSAGES.REQUEST_ERROR}</div>;

  return (
    <div>
      <div className="max-w-3xl mx-auto p-6 shadow-md">
        <div className="flex items-center h-12 mb-4 gap-36 md:gap-80">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="pr-4 py-2 text-primary hover:text-green-600"
          >
            <HiOutlineArrowLeft />
          </button>
          <h2 className="transform -translate-x-1/2 text-xl text-primary font-semibold">
            Detailed View
          </h2>
        </div>
        <Formik
          enableReinitialize
          initialValues={{
            site_name: attendance.site_name,
            user_name: attendance.user_name,
            date: attendance.date,
            checkin_time: attendance.checkin_time,
            checkout_time: attendance.checkout_time,
            is_backdated: attendance.is_backdated,
            status: attendance.status
              .toLowerCase()
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
          }}
        >
          <Form>
            <FloatingInput
              label="User Name"
              name="user_name"
              type="text"
              disabled={true}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <FloatingInput
                label="Site Name"
                name="site_name"
                type="text"
                disabled={true}
              />
              <FloatingInput
                label="Date"
                name="date"
                type="text"
                disabled={true}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <FloatingInput
                label="Check In Time"
                name="checkin_time"
                value={formatTimeAMPM(attendance.checkin_time)}
                type="text"
                disabled={true}
              />
              <FloatingInput
                label="Check Out Time"
                name="checkout_time"
                value={
                  attendance.checkout_time
                    ? formatTimeAMPM(attendance.checkout_time)
                    : "N/A"
                }
                type="text"
                disabled={true}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-3">
              <FloatingInput
                label="Backdated"
                name="is_backdated"
                disabled={true}
              />
              <FloatingInput
                label="Status"
                name="status"
                type="text"
                disabled={true}
              />
            </div>
          </Form>
        </Formik>
        <div className="flex flex-col md:flex-row w-full justify-around gap-2">
          {/* Check-In Photo */}
          <div className="w-full md:w-1/2">
            <div className="text-primary mb-2 flex items-center gap-2">
              <span>CheckIn Photo</span>
              {attendance?.checkin_photo_url && (
                <button
                  type="button"
                  className="text-primary underline flex items-center gap-1"
                  onClick={() => setShowCheckinPhoto(!showCheckinPhoto)}
                >
                  {showCheckinPhoto ? (
                    <div className="flex items-center gap-1">
                      <BiHide />
                      <span>Hide Photo</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <HiViewfinderCircle />
                      View Photo
                    </div>
                  )}
                </button>
              )}
            </div>
            {attendance?.checkin_photo_url ? (
              showCheckinPhoto && (
                <div className="w-full flex justify-center">
                  <img
                    src={attendance.checkin_photo_url}
                    alt="check_in"
                    className="w-full max-h-96 object-contain rounded border"
                  />
                </div>
              )
            ) : (
              <div className="text-secondary">
                {MESSAGES.PHOTO_NOT_AVAILABLE}
              </div>
            )}
          </div>

          {/* Check-Out Photo */}
          <div className="w-full md:w-1/2">
            <div className="text-primary mb-2 flex items-center gap-2">
              {" "}
              <span>Checkout Photo</span>
              {attendance?.checkout_photo_url && (
                <button
                  type="button"
                  className="text-primary underline flex items-center gap-1"
                  onClick={() => setShowCheckoutPhoto(!showCheckoutPhoto)}
                >
                  {showCheckoutPhoto ? (
                    <div className="flex items-center gap-1">
                      <BiHide />
                      <span>Hide Photo</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <HiViewfinderCircle />
                      View Photo
                    </div>
                  )}
                </button>
              )}
            </div>
            {attendance.checkout_photo_url ? (
              showCheckoutPhoto && (
                <div className="w-full flex justify-center">
                  <img
                    src={attendance.checkout_photo_url}
                    alt="check_out"
                    className="w-full max-h-96 object-contain rounded border"
                  />
                </div>
              )
            ) : (
              <div className="text-secondary">
                {MESSAGES.PHOTO_NOT_AVAILABLE}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-primary mb-2">Location</div>
          {(!attendance.checkin_latitude || !attendance.checkin_longitude) &&
          (!attendance.checkout_latitude || !attendance.checkout_longitude) ? (
            <div className="text-secondary">
              {MESSAGES.LOCATION_NOT_AVAILABLE}
            </div>
          ) : isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={{
                lat: parseFloat(attendance?.checkin_latitude),
                lng: parseFloat(attendance?.checkin_longitude),
              }}
              zoom={18}
              options={{
                gestureHandling: "none",
                draggable: false,
                scrollwheel: false,
                disableDoubleClickZoom: true,
                keyboardShortcuts: false,
              }}
            >
              {sitePolygon.length > 0 && (
                <Polygon
                  paths={sitePolygon.map(([lng, lat]) => ({ lat, lng }))}
                  options={{
                    fillColor: "#00BFFF",
                    fillOpacity: 0.3,
                    strokeColor: "#1E90FF",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              )}

              {attendance?.checkin_latitude &&
                attendance?.checkin_longitude && (
                  <Marker
                    position={{
                      lat: parseFloat(attendance?.checkin_latitude),
                      lng: parseFloat(attendance?.checkin_longitude),
                    }}
                    label="Check-In"
                  />
                )}

              {attendance?.checkout_latitude &&
                attendance?.checkout_longitude && (
                  <Marker
                    position={{
                      lat: parseFloat(attendance.checkout_latitude),
                      lng: parseFloat(attendance.checkout_longitude),
                    }}
                    label="Check-Out"
                  />
                )}
            </GoogleMap>
          ) : (
            <div className="flex justify-center pt-10">
              <Spinner size={4} />
            </div>
          )}
        </div>

        {attendance.status !== ACTION_APPROVED &&
          attendance.status !== ACTION_REJECTED &&
          attendance.status !== ACTION_AUTO_APPROVED &&
          nonStaffAccess && (
            <div className="flex justify-center gap-4 pt-4 px-4">
              <button
                className={`px-4 py-2 text-white rounded ${
                  canApproveReject()
                    ? "bg-primary hover:bg-secondary"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
                onClick={() => handleApproval(attendance.id, ACTION_APPROVED)}
                disabled={!canApproveReject()}
              >
                Approve
              </button>
              <button
                className={`px-4 py-2 text-white rounded ${
                  canApproveReject()
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
                disabled={!canApproveReject()}
              >
                Reject
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default DetailedViewPage;
